import Card from '@amzn/meridian/card';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import SideMenu, { SideMenuLink, SideMenuTitle } from '@amzn/meridian/side-menu';
import styled from '@emotion/styled';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { Text, useTranslation } from '../components/blocks';
import { DownloadHelpButton } from '../components/composites/DownloadHelpButton';
import { IntroductionHelp } from '../components/constructed/Help';
import { HelpComponentsMap, HelpSection, HelpSections } from '../constants';

const ContentContainer = styled.div`
    flex: 1;
    min-width: 0;
`;

const OuterContainer = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
`;

const HelpContainer = styled.div`
    width: 100%;
    max-width: 100vw;
    padding: 0 1rem;
    margin: 0 auto;

    @media (min-width: 768px) {
        max-width: 80vw;
    }
`;

/**
 * Forces consistent width across all help components.
 * Meridian's Column/Row components and Card component was causing help pages with less content to appear narrower.
 * This spacer is needed to ensure the content fills the available width regardless of content size.
 * Accounts for SideMenu width (250px) when open.
 */
const WidthSpacer = styled.div<{ menuOpen: boolean }>`
    height: 0;
    min-width: ${({ menuOpen }) => (menuOpen ? 'calc(100vw - 250px - 2rem)' : 'calc(100vw - 2rem)')};

    @media (min-width: 768px) {
        min-width: ${({ menuOpen }) => (menuOpen ? 'calc(80vw - 250px - 2rem)' : 'calc(80vw - 2rem)')};
    }
`;

const HelpPage = () => {
    const [menuOpen, setMenuOpen] = useState(true);
    const [openLinkId, setOpenLinkId] = useState<string | null>(null);
    const helpContentRef = useRef<HTMLDivElement>(null);
    const { t } = useTranslation('introductionHelp');
    const location = useLocation();
    const history = useHistory();

    const getIdFromHash = () => {
        const hash = location.hash.slice(1);
        return HelpComponentsMap[hash] ? hash : 'introduction';
    };
    const [selectedId, setSelectedId] = useState(getIdFromHash);
    const CurrentHelp = HelpComponentsMap[selectedId] || IntroductionHelp;

    const handleSelect = (id: string) => {
        setSelectedId(id);
        history.replace(`#${id}`);
    };

    useEffect(() => {
        const hash = location.hash.slice(1);
        if (HelpComponentsMap[hash] && hash !== selectedId) {
            setSelectedId(hash);
        }
    }, [location.hash, selectedId]);

    const renderSection = (section: HelpSection) => {
        if (section.children) {
            return (
                <SideMenuLink
                    key={section.id}
                    selected={section.children.some((c) => c.id === selectedId)}
                    open={openLinkId === section.id}
                    onClick={() => setOpenLinkId(openLinkId === section.id ? null : section.id)}
                >
                    {t(section.labelKey)}
                    {section.children.map((child) => (
                        <SideMenuLink
                            key={child.id}
                            selected={selectedId === child.id}
                            onClick={() => handleSelect(child.id)}
                            onEscape={() => setOpenLinkId(null)}
                        >
                            {t(child.labelKey)}
                        </SideMenuLink>
                    ))}
                </SideMenuLink>
            );
        }
        return (
            <SideMenuLink
                key={section.id}
                selected={selectedId === section.id}
                onClick={() => handleSelect(section.id)}
            >
                {t(section.labelKey)}
            </SideMenuLink>
        );
    };

    return (
        <OuterContainer>
            <HelpContainer>
                <Row alignmentVertical={'stretch'} spacing={'none'}>
                    <SideMenu
                        open={menuOpen}
                        onOpen={() => setMenuOpen(true)}
                        onClose={() => setMenuOpen(false)}
                        type={'skinny'}
                        width={250}
                    >
                        <SideMenuTitle>
                            <Text type={'h200'}>{t('menu_title')}</Text>
                        </SideMenuTitle>
                        {HelpSections.map(renderSection)}
                    </SideMenu>
                    <ContentContainer>
                        <Card>
                            <WidthSpacer menuOpen={menuOpen} />
                            <Row alignmentHorizontal={'right'}>
                                <DownloadHelpButton
                                    contentRef={helpContentRef}
                                    filename={`Reseller-Portal-Help-${selectedId}.docx`}
                                />
                            </Row>
                            <Column ref={helpContentRef} spacing={'medium'}>
                                <CurrentHelp callback={handleSelect} />
                            </Column>
                        </Card>
                    </ContentContainer>
                </Row>
            </HelpContainer>
        </OuterContainer>
    );
};

export { HelpPage };
